import { useMemo } from 'react';

import dayjs from 'dayjs';
import meanBy from 'lodash/meanBy';

import { useInventoryWorkloadsMetrics } from '../../_hooks/useInventoryWorkloadsMetrics';

export type ResourceUtilizationPoint = {
  timestamp: string;
  chartTimestampUnix: number;
  cpu: number;
  memory: number;
};

const utilizationPct = (usage: number, req: number): number =>
  req === 0 ? 0 : (usage / req) * 100;

export const useResourceUtilizationData = () => {
  const { dataOrFixture, isLoading, isFetching, error, refetch } =
    useInventoryWorkloadsMetrics();

  const { points, cpuPct, memoryPct } = useMemo(() => {
    if (!dataOrFixture) {
      return {
        points: [] as ResourceUtilizationPoint[],
        cpuPct: undefined as number | undefined,
        memoryPct: undefined as number | undefined,
      };
    }

    const { cpu, memory } = dataOrFixture;
    const length = Math.min(cpu.length, memory.length);

    const points: ResourceUtilizationPoint[] = Array.from(
      { length },
      (_, index) => {
        const cpuItem = cpu[index];
        const memoryItem = memory[index];

        return {
          timestamp: cpuItem.timestamp,
          chartTimestampUnix: dayjs(cpuItem.timestamp).unix(),
          cpu: utilizationPct(cpuItem.usage, cpuItem.req),
          memory: utilizationPct(memoryItem.usage, memoryItem.req),
        };
      }
    );

    const avgCpuUsage = meanBy(cpu, 'usage') ?? 0;
    const avgCpuReq = meanBy(cpu, 'req') ?? 0;
    const avgMemoryUsage = meanBy(memory, 'usage') ?? 0;
    const avgMemoryReq = meanBy(memory, 'req') ?? 0;

    return {
      points,
      cpuPct: cpu.length ? utilizationPct(avgCpuUsage, avgCpuReq) : undefined,
      memoryPct: memory.length
        ? utilizationPct(avgMemoryUsage, avgMemoryReq)
        : undefined,
    };
  }, [dataOrFixture]);

  return {
    points,
    cpuPct,
    memoryPct,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};
